
import ECharts from 'vue-echarts'
import 'echarts/lib/chart/bar'
import 'echarts/lib/component/tooltip'
import { graphic } from 'echarts/core'
import 'zrender/lib/canvas/canvas'
import numbro from 'numbro'

import TextBox from '@/components/TextBox.vue'
import PercentChip from '@/components/PercentChip.vue'
import { defaultComponentDictionary, mergeComponentDictionary } from '@/utils/componentDictionary.js'

export default {
  data () {
    return {
    }
  },
  computed: {
    defaultDictionary () {
      return defaultComponentDictionary(key => this.$t(key))
    },
    dataComponents () {
      if (this.data.components) {
        return this.data.components
      } else {
        return this.data.outputs[Object.keys(this.data.outputs)[0]].components
      }
    },
    options () {
      let series = [
        {
          name: 'Invisible',
          type: 'bar',
          stack: 'stack',
          itemStyle: {
            borderColor: 'rgba(0,0,0,0)',
            color: 'rgba(0,0,0,0)'
          },
          data: this.salesTransparentArray
        },
        {
          name: 'Values',
          type: 'bar',
          stack: 'stack',
          label: {
            show: true,
            position: 'top',
            formatter: this.formatEchartsValue,
            align: 'center',
            rich: {
              b: {
                color: 'lightgray',
                fontSize: 11
              }
            }
          },
          labelLayout: {
            hideOverlap: true
          },
          itemStyle: {
            // color: this.color ? this.color : ''
            color: new graphic.LinearGradient(0, 2, 0, 0, [
              { offset: 0, color: 'white' },
              { offset: 1, color: this.color ? this.color : '' }
            ]),
            borderRadius: [2, 2, 0, 0]
          },
          data: this.salesComponentsArray
        },
        {
          name: 'Negative Values',
          type: 'bar',
          stack: 'stack',
          label: {
            show: true,
            position: 'top',
            formatter: this.formatEchartsValue,
            align: 'center',
            rich: {
              b: {
                color: 'lightgray',
                fontSize: 11
              }
            }
          },
          labelLayout: {
            hideOverlap: true
          },
          itemStyle: {
            // color: 'rgb(194, 53, 49)'
            color: new graphic.LinearGradient(0, 2, 0, 0, [
              { offset: 0, color: 'white' },
              { offset: 1, color: 'rgb(194, 53, 49)' }
            ]),
            borderRadius: [2, 2, 0, 0]
          },
          data: this.negativeSalesComponentsArray
        }
      ]
      return {
        textStyle: {
          fontFamily: '"Roboto","Helvetica",sans-serif',
          lineHeight: 1.2381,
          fontWeight: 400
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          splitLine: {
            show: false
          },
          axisLabel: {
            rotate: 45
          },
          data: this.componentNamesArray.names
        },
        yAxis: {
          type: 'value',
          splitLine: {
            show: false
          },
          axisLabel: {
            formatter: this.formatValue
          }
        },
        tooltip: {
          trigger: 'axis',
          formatter: (params) => {
            // Filter out the invisible series and find the series with actual data
            const validParam = params.find(param => param.seriesName !== 'Invisible' && param.value !== '-')

            if (!validParam) {
              return ''
            }

            const dataIndex = validParam.dataIndex
            const componentName = this.componentNamesArray.names[dataIndex]
            let value = this.componentNamesArray.values[dataIndex]
            const formattedValue = this.formatValue(value)

            let tooltip = `<b>${componentName}</b><br/>${formattedValue} ${this.unit}`

            if (this.showPercentages && this.total && this.total > 0) {
              const percentage = Math.round(value / this.total * 100)
              tooltip += ` (${percentage}%)`
            }

            return tooltip
          }
        },
        toolbox: {
          show: this.export,
          left: 0,
          feature: {
            dataView: {
              readOnly: true,
              title: this.$t('components.simulationDetails.SalesDecomposition.dataView'),
              lang: [this.$t('components.simulationDetails.SalesDecomposition.dataView'), this.$t('components.simulationDetails.SalesDecomposition.close'), this.$t('components.simulationDetails.SalesDecomposition.refresh')]
            },
            saveAsImage: {
              type: 'png',
              name: this.exportImageName || this.title || this.$t('components.simulationDetails.SalesDecomposition.salesDecomposition', { kpi: this.customKpiName }),
              title: this.$t('components.simulationDetails.SalesDecomposition.saveAsImage')
            }
          },
          showTitle: false,
          tooltip: {
            show: true,
            formatter: function (param) {
              return param.title
            }
          }
        },
        series: this.colors ? this.coloredSeries : series
      }
    },
    coloredSeries () {
      let series = [
        {
          name: 'Invisible',
          type: 'bar',
          stack: 'stack',
          itemStyle: {
            borderColor: 'rgba(0,0,0,0)',
            color: 'rgba(0,0,0,0)'
          },
          data: this.salesTransparentArray
        }
      ]
      for (const index in this.componentNamesArray.names) {
        let newArray = new Array(this.componentNamesArray.names.length)
        newArray[index] = this.componentNamesArray.values[index]
        series.push({
          name: 'Values',
          type: 'bar',
          stack: 'stack',
          label: {
            show: true,
            position: 'top',
            formatter: this.formatEchartsValue,
            rich: {
              b: {
                color: 'lightgray',
                fontSize: 11
              }
            },
            align: 'center',
            color: this.colors[this.componentNamesArray.names[index]] ? this.colors[this.componentNamesArray.names[index]] : '#6950A1'
          },
          labelLayout: {
            hideOverlap: true
          },
          itemStyle: {
            // color: this.colors && this.componentNamesArray.names[index] in this.colors ? this.colors[this.componentNamesArray.names[index]] : '#6950a1'
            color: this.colors && this.componentNamesArray.names[index] in this.colors ? new graphic.LinearGradient(0, 2, 0, 0, [
              { offset: 0, color: 'white' },
              { offset: 1, color: this.colors[this.componentNamesArray.names[index]] }
            ]) : new graphic.LinearGradient(0, 2, 0, 0, [
              { offset: 0, color: 'white' },
              { offset: 1, color: '#6950A1' }
            ]),
            borderRadius: [2, 2, 0, 0]
          },
          data: newArray
        })
      }
      return series
    },
    componentNamesArray () {
      let names = []
      let values = []
      let sortedComponents = []
      // Since schemas: report.v1 and scenario.v3, Sales Decomposition Components must come in a specific order
      // They come in this format: components = [{ name: 'Component name', value: 123 },...]
      if (Array.isArray(this.dataComponents)) {
        // report.v1
        sortedComponents = this.dataComponents.map(component => {
          return [component.name, component.value]
        })
      } else if (this.dataComponents.components && Array.isArray(this.dataComponents.components)) {
        // scenario.v3
        sortedComponents = this.dataComponents.components.map(component => {
          return [component.name, component.value]
        })
      } else {
        // Old versions
        sortedComponents = Object.entries(this.dataComponents).sort((a, b) => {
          return Math.abs(b[1]) - Math.abs(a[1])
        })
      }
      // Merge default dictionary and provided dictionary object (conflicts could appear if same names are used)
      // TODO: Ensure no name conflicts can occur
      const dictionary = mergeComponentDictionary(this.defaultDictionary, this.dictionary)
      sortedComponents.forEach(component => {
        if (typeof component[1] === 'number') {
          if (dictionary && component[0] in dictionary) {
            names.push(dictionary[component[0]])
          } else {
            names.push(component[0])
          }
          values.push(component[1])
        } else if (typeof component[1] === 'object') {
          Object.entries(component[1]).forEach(subcomponent => {
            if (dictionary && subcomponent[0] in dictionary) {
              names.push(dictionary[subcomponent[0]])
            } else {
              names.push(subcomponent[0])
            }
            values.push(subcomponent[1])
          })
        }
      })
      if (this.total) {
        names.push(this.$t('components.simulationDetails.SalesDecomposition.total'))
        values.push(this.total)
      } else {
        console.log('Warning: No total given')
      }
      return {
        names: names,
        values: values
      }
    },
    salesComponentsArray () {
      var array = [...this.componentNamesArray.values]
      for (var index in array) {
        if (array[index] < 0) {
          array[index] = '-'
        }
      }
      return array
    },
    negativeSalesComponentsArray () {
      var array = [...this.componentNamesArray.values]
      for (var index in array) {
        if (array[index] >= 0) {
          array[index] = '-'
        } else {
          array[index] *= -1
        }
      }
      return array
    },
    negativePositionsArray () {
      var array = [...this.componentNamesArray.values]
      for (var index in array) {
        if (array[index] >= 0) {
          array[index] = false
        } else {
          array[index] = true
        }
      }
      return array
    },
    salesTransparentArray () {
      var array = new Array(this.componentNamesArray.values.length)
      var accumulated = 0
      for (var i = 0; i < array.length; i++) {
        if (i === 0) {
          array[0] = 0
        } else if (i < array.length - 1) {
          if (this.negativePositionsArray[i]) {
            accumulated = accumulated - this.negativeSalesComponentsArray[i]
          }
          if (!this.negativePositionsArray[i - 1]) {
            accumulated += this.salesComponentsArray[i - 1]
          }
          array[i] = accumulated
        } else {
          array[i] = 0
        }
      }
      return array
    },
    total: function () {
      var total = this.data.total || Object.values(this.dataComponents).reduce((a, b) => a + b)
      return total
    },
    chartWidth: function () {
      return this.salesComponentsArray.length * 60
    }
  },
  methods: {
    formatValue: function (value, mantissa = 1) {
      return numbro(value).format({
        average: true,
        lowPrecision: false,
        mantissa: mantissa,
        optionalMantissa: true,
        trimMantissa: true
      })
    },
    formatEchartsValue: function (obj) {
      var value = obj.value
      if (this.negativePositionsArray[obj.dataIndex]) {
        value = -1 * obj.value
      }
      let formatted = this.formatValue(value)
      if (this.showPercentages && this.total && this.total > 0) {
        let percentage = Math.round(value / this.total * 100)
        formatted = `${formatted}\n{b| ${percentage}%}`
      }
      return formatted
    }
  },
  props: {
    data: {
      type: Object,
      required: true
    },
    precision: {
      type: Number,
      required: false
    },
    color: {
      type: String,
      required: false,
      default: function () {
        return '#6950a1'
      }
    },
    unit: {
      type: String,
      required: false,
      default: function () {
        return 'USD'
      }
    },
    dictionary: {
      type: Object,
      required: false
    },
    title: {
      type: String,
      required: false
    },
    // Drops the header TextBox would otherwise draw, for a caller that titles the chart itself. The
    // info-dialog slot lives in that header, so a caller hiding it owns the explanation too.
    hideTitle: {
      type: Boolean,
      required: false,
      default: function () {
        return false
      }
    },
    exportImageName: {
      type: String,
      required: false
    },
    colors: {
      type: Object,
      required: false
    },
    export: {
      type: Boolean,
      required: false,
      default: function () {
        return false
      }
    },
    showPercentages: {
      type: Boolean,
      required: false,
      default: function () {
        return false
      }
    },
    customKpiName: {
      type: String,
      required: true
    }
  },
  components: {
    'v-chart': ECharts,
    TextBox,
    PercentChip
  }
}
